/* eslint-disable react/forbid-elements */
import React, { HTMLAttributes } from 'react'

import { breakpointToPixels } from '@emico/styles'
import { last } from '@emico/utils'

import getImaginaryUrl, {
  ImaginaryActions,
  ImaginaryParams,
} from './getImaginaryUrl'
import getResolutionVariations from './getResolutionVariations'

export interface ImageSizes {
  [key: string]: number | 'auto'
}

export interface ImagePreset {
  sizes?: ImageSizes
  width?: number
  height?: number
}

export interface ImageProps extends HTMLAttributes<HTMLImageElement> {
  /** URL of the image */
  url: string

  /** Width of the image, used when generating high resolution image variants. Optional but recommended. */
  width?: number

  /** Height of the image, used when generating high resolution image variants. Optional but recommended. */
  height?: number

  /** Optional property for ratio calculations when only providing one of width or height */
  originalHeight?: number

  /** Optional property for ratio calculations when only providing one of width or height */
  originalWidth?: number

  /**
   * Image sizes can be defined using devices (mobile, tablet, desktop),
   * breakpoints defined in layout.ts e.g. xs, sm, md, lg, xl or using
   * pixels. Using `{ '1500': ... }` as a breakpoint will add a
   * `(min-width: 1500px)` size breakpoint.
   * The value you define will be the pixel size of the image, you can
   * set this to `auto` if you want to keep this in sync with the breakpoints.
   */
  sizes?: ImageSizes

  /** A description of the Image, if the image is purely decorational with no meaning this should be an empty string. */
  alt: string

  /** Extra imaginary configuration when generated resized images. */
  imaginaryConfig?: Partial<ImaginaryParams>

  /** Get width, height and sizes from a preset */
  preset?: ImagePreset

  /** @deprecated Use `lazy` boolean instead. In the future `lazy` will become a required prop. */
  loading?: 'eager' | 'lazy'

  /**
   * The 'lazy' prop is used to add the native loading="lazy" directive to the img element.
   *
   * Using the native lazy loading implementation has some advantages:
   * Deferred loading of images still works even if JavaScript is disabled on the client
   * Supported by most popular Chromium-powered browsers, webkit implementation is in progress
   * The directive will be ignored by unsupported browsers
   *
   * Please consider:
   * When setting loading="lazy" keep in mind that width and height should be set to prevent a layout shift
   * Usage of loading="lazy" should be avoided for elements visible in the first viewport, since it will result in a visible delay of the image load
   * For more info see: https://web.dev/browser-level-image-lazy-loading/#avoid-lazy-loading-images-that-are-in-the-first-visible-viewport
   *
   * This will become a required prop in the future
   */
  lazy?: boolean
}

export function calculateImageWidthHeight(
  width?: number,
  height?: number,
  originalWidth?: number,
  originalHeight?: number,
) {
  // If originalHeight and originalWidth are provided, we can calculate a ratio
  const ratio =
    originalHeight && originalWidth ? originalHeight / originalWidth : undefined

  // Use the ratio to calculate the final width and height of the image to
  // prevent layout after image load. Rounding is done to mimic Imaginary
  const finWidth =
    width ?? (ratio && height ? Math.round(height) * (1 / ratio) : undefined)
  const finHeight =
    height ?? (ratio && width ? Math.round(width) * ratio : undefined)

  // Imaginary rounds width/height when not round
  const roundedWidth = finWidth ? Math.round(finWidth) : finWidth
  const roundedHeight = finHeight ? Math.round(finHeight) : finHeight

  return [roundedWidth, roundedHeight]
}

const Image = ({
  url,
  width,
  height,
  originalHeight,
  originalWidth,
  sizes: sizesParam,
  imaginaryConfig,
  alt,
  preset,
  loading,
  lazy,
  ...props
}: ImageProps) => {
  const config: ImaginaryParams = {
    action: ImaginaryActions.RESIZE,
    url,
    width: width ?? preset?.width,
    height: height ?? preset?.height,
    extend: 'white',
    ...(imaginaryConfig ?? {}),
  }

  const sizes = sizesParam ?? preset?.sizes

  // If no width or height is specified, then Imaginary will be used to
  // optimize the image.
  if (!width && !height && !sizes) {
    const src = getImaginaryUrl({
      ...(imaginaryConfig ?? {}),
      action: imaginaryConfig?.action ?? ImaginaryActions.CONVERT,
      url,
    })
    return <img src={src} alt={alt} {...props} />
  }

  // If sizes is defined, resolution switching is done based of sizes.
  if (sizes) {
    const breakpoints = Object.entries(sizes).map(([breakpoint, width]) => {
      const pixels = breakpointToPixels(breakpoint)
      const realWidth = width === 'auto' ? last(pixels) : width
      const sizeUrl = getImaginaryUrl({
        action: ImaginaryActions.RESIZE,
        url,
        width: realWidth,
        ...imaginaryConfig,
      })
      return { breakpoint: pixels[0], width: realWidth, url: sizeUrl }
    })

    // Sort breakpoints from large to small
    breakpoints.sort((a, b) => b.breakpoint - a.breakpoint)

    /**
     * Generate `sizes` and `srcSet` attributes:
     * sizes:  [...(min-width: {breakpoint}px) {width}px], {width}px
     * srcSet: [...{url} {width}w]
     */
    const fallback = last(breakpoints)
    const sizesAttr = breakpoints
      .slice(0, -1) // The last breakpoint will also be the fallback
      .map((o) => `(min-width: ${o.breakpoint}px) ${o.width}px`)

    sizesAttr.push(`${fallback.width}px`)

    const srcSet = breakpoints.map((o) => `${o.url} ${o.width}w`).join(', ')

    return (
      <img
        srcSet={srcSet}
        src={fallback.url}
        sizes={sizesAttr.join(', ')}
        alt={alt}
        width={width}
        height={height}
        {...props}
        loading={lazy ? 'lazy' : loading}
      />
    )
  }

  // Width should be defined here because it is checked earlier
  const sources = getResolutionVariations(config)

  const [roundedWidth, roundedHeight] = calculateImageWidthHeight(
    width,
    height,
    originalWidth,
    originalHeight,
  )

  return (
    <img
      srcSet={`${sources[1]} 2x`}
      src={sources[0]}
      alt={alt}
      width={roundedWidth}
      height={roundedHeight}
      {...props}
      loading={lazy ? 'lazy' : loading}
    />
  )
}

export default Image
